import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Trash2, MailOpen, Mail, ShieldAlert, RefreshCw } from "lucide-react";
import api, { formatApiError } from "../../lib/api";

export default function AdminContacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSpam, setShowSpam] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/contacts").then((r) => setContacts(r.data)).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const markRead = async (c, is_read) => {
    try {
      await api.patch(`/admin/contacts/${c.id}`, { is_read });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await api.delete(`/admin/contacts/${id}`);
      toast.success("Message deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const visible = contacts.filter((c) => showSpam || !c.is_spam);

  return (
    <div data-testid="admin-contacts-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-brand-emerald">Contact Inbox</h1>
          <p className="text-brand-muted text-sm mt-1">{visible.length} message{visible.length !== 1 ? "s" : ""}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} data-testid="contacts-refresh-btn" className="p-2.5 rounded-xl bg-white border border-[#D6E4DC] text-brand-emerald hover:bg-brand-mint" aria-label="Refresh"><RefreshCw className="w-4 h-4" /></button>
          <button
            onClick={() => setShowSpam(!showSpam)}
            data-testid="contacts-toggle-spam"
            className={`px-4 py-2 rounded-full text-xs font-bold transition-colors flex items-center gap-1.5 ${showSpam ? "bg-red-100 text-red-700" : "bg-white text-brand-muted border border-[#D6E4DC]"}`}
          >
            <ShieldAlert className="w-3.5 h-3.5" /> {showSpam ? "Showing spam" : `Spam (${contacts.filter((c) => c.is_spam).length})`}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-10 h-10 border-4 border-brand-emerald border-t-brand-lime rounded-full animate-spin" /></div>
      ) : visible.length === 0 ? (
        <div className="bg-brand-mint rounded-2xl border border-dashed border-brand-emerald/20 p-12 text-center text-brand-muted" data-testid="contacts-empty">No messages here.</div>
      ) : (
        <div className="space-y-3">
          {visible.map((c) => (
            <div key={c.id} className={`bg-white rounded-2xl border p-5 ${c.is_read ? "border-[#D6E4DC] opacity-75" : "border-brand-lime/50 shadow-sm"} ${c.is_spam ? "border-red-200 bg-red-50/40" : ""}`} data-testid={`contact-row-${c.id}`}>
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="font-bold text-brand-ink">{c.name}</span>
                    {!c.is_read && !c.is_spam && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-brand-lime/20 text-brand-lime-dark">New</span>}
                    {c.is_spam && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-100 text-red-700 flex items-center gap-1"><ShieldAlert className="w-3 h-3" /> Spam</span>}
                  </div>
                  <p className="text-xs text-brand-muted mb-2">
                    <a href={`mailto:${c.email}`} className="text-brand-emerald font-semibold hover:text-brand-lime">{c.email}</a>
                    {c.phone && <> · {c.phone}</>} · {new Date(c.created_at).toLocaleString("en-NG")}
                  </p>
                  <p className="font-semibold text-sm text-brand-emerald mb-1">{c.subject}</p>
                  <p className="text-sm text-brand-ink leading-relaxed whitespace-pre-wrap">{c.message}</p>
                </div>
                <div className="flex md:flex-col gap-2 shrink-0">
                  <button onClick={() => markRead(c, !c.is_read)} data-testid={`contact-read-${c.id}`} className="px-3 py-1.5 rounded-lg bg-brand-mint text-brand-emerald text-xs font-bold hover:bg-brand-lime/30 flex items-center gap-1 transition-colors">
                    {c.is_read ? <><Mail className="w-3.5 h-3.5" /> Unread</> : <><MailOpen className="w-3.5 h-3.5" /> Mark read</>}
                  </button>
                  <button onClick={() => remove(c.id)} data-testid={`contact-delete-${c.id}`} className="px-3 py-1.5 rounded-lg bg-red-50 text-red-600 text-xs font-bold hover:bg-red-100 flex items-center gap-1 transition-colors"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
